import { createContext, type ReactNode, type RefObject, useCallback, useContext, useEffect, useMemo, useRef } from "react";
import type { ScrollView as RNScrollView, View as RNView } from "react-native";
import { isWeb, type TamaguiElement } from "tamagui";
const DEFAULT_SCROLL_DELAY_MS = 350;
type ScrollToViewOptions = {
  /** Delay in ms before measuring and scrolling. Defaults to 350 to let layout animations settle. */
  delay?: number;
};
type ModalScrollContextValue = {
  scrollToView: (ref: RefObject<TamaguiElement | null>, options?: ScrollToViewOptions) => void;
  /**
   * Register an extra dismisser called on tap-outside (in addition to
   * `Keyboard.dismiss()`) for custom widgets like Stripe CardForm that
   * resignFirstResponder can't reach. Returns an unregister fn.
   */
  registerKeyboardDismisser: (dismisser: () => void) => () => void;
  /** Internal: invoked by KeyboardAwareBody on tap-outside. */
  runKeyboardDismissers: () => void;
};
const ModalScrollContext = createContext<ModalScrollContextValue | null>(null);
export const ModalScrollProvider = ({
  scrollViewRef,
  children
}: {
  scrollViewRef: RefObject<RNScrollView | null> | null;
  children: ReactNode;
}) => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dismissersRef = useRef<Set<() => void>>(new Set());
  useEffect(() => {
    return () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
  const registerKeyboardDismisser = useCallback((dismisser: () => void) => {
    dismissersRef.current.add(dismisser);
    return () => {
      dismissersRef.current.delete(dismisser);
    };
  }, []);
  const runKeyboardDismissers = useCallback(() => {
    for (const dismisser of dismissersRef.current) {
      try {
        dismisser();
      } catch {
        // Don't let one dismisser block the others.
      }
    }
  }, []);
  const scrollToView = useCallback((targetRef: RefObject<TamaguiElement | null>, options?: ScrollToViewOptions) => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      const node = targetRef.current;
      if (!node) {
        return;
      }
      if (isWeb) {
        (node as unknown as HTMLElement).scrollIntoView({
          behavior: "smooth",
          block: "nearest"
        });
        return;
      }

      // Native: measure target position relative to scroll view and scroll to it
      const scrollNode = scrollViewRef?.current;
      if (!scrollNode) {
        return;
      }
      (node as unknown as RNView).measureLayout(scrollNode as unknown as RNView, (_x: number, y: number) => {
        scrollNode.scrollTo({
          y,
          animated: true
        });
      }, () => {});
    }, options?.delay ?? DEFAULT_SCROLL_DELAY_MS);
  }, [scrollViewRef]);
  const value = useMemo(() => ({
    scrollToView,
    registerKeyboardDismisser,
    runKeyboardDismissers
  }), [scrollToView, registerKeyboardDismisser, runKeyboardDismissers]);
  return <ModalScrollContext.Provider value={value} data-sentry-element="ModalScrollContext.Provider" data-sentry-component="ModalScrollProvider" data-sentry-source-file="ModalScrollContext.tsx">
      {children}
    </ModalScrollContext.Provider>;
};
export const useModalScroll = (): ModalScrollContextValue => {
  const ctx = useContext(ModalScrollContext);
  if (!ctx) {
    throw new Error("useModalScroll must be used within a scrollable Modal");
  }
  return ctx;
};